import { FormGroup, FormControl, FormArray, AbstractControl, ValidatorFn } from '@angular/forms';

export type FormType<T> = FormGroup<{
  [K in keyof T]: T[K] extends any[]
  ? FormArray<T[K][number] extends object ? FormType<T[K][number]> : FormControl<T[K][number]>>
  : T[K] extends object
  ? FormType<T[K]>
  : FormControl<T[K]>
}> & {
  hasChanges: (deepComparison?: boolean) => boolean;
  setInitialState: () => void;
};


export function createFormGroup<T>(defaultValues: T, validators?: Record<string, ValidatorFn[]>): FormType<T> {
  const form: FormType<typeof defaultValues> = getForm(defaultValues, validators);

  let currentState: any;

  form.setInitialState = () => {
    return currentState = getValue(form);
  }

  form.hasChanges = (deepComparison: boolean = false) => {
    return currentState ? hasChanges(form, currentState, deepComparison) : false;
  };

  return form;
}

/**
 * Wraps an existing FormGroup or FormArray with form-builder utilities (setInitialState, hasChanges).
 * Allows using Angular FormBuilder to create nested forms and then add utility methods.
 * @param form An existing FormGroup or FormArray.
 * @returns The same form with added utility methods.
 */
export function createNestedFormGroup<T extends FormGroup | FormArray>(form: T): T & {
  setInitialState: () => void;
  hasChanges: (deepComparison?: boolean) => boolean;
  getForm: () => T;
} {
  let currentState: any;

  function serialize(control: AbstractControl<any, any>): any {
    if (control instanceof FormGroup) {
      return Object.keys(control.controls).reduce((acc, key) => {
        acc[key] = serialize(control.get(key)!);
        return acc;
      }, {} as Record<string, any>);
    }
    if (control instanceof FormArray) {
      return control.controls.map(serialize);
    }
    return { value: control.getRawValue(), disabled: control.disabled };
  }

  function compare(control: AbstractControl<any, any>, initial: any): boolean {
    if (control instanceof FormGroup) {
      return Object.keys(control.controls).some(key => compare(control.get(key)!, initial?.[key]));
    }
    if (control instanceof FormArray) {
      return control.controls.some((ctrl, idx) => compare(ctrl, initial?.[idx]));
    }
    return control.value !== initial?.value || control.disabled !== initial?.disabled;
  }

  (form as any).setInitialState = () => {
    currentState = serialize(form);
  };

  (form as any).hasChanges = () => {
    return currentState ? compare(form, currentState) : false;
  };

  (form as any).getForm = () => form;

  return form as T & {
    setInitialState: () => void;
    hasChanges: (deepComparison?: boolean) => boolean;
    getForm: () => T;
  };
}

function getForm(value: any, validators?: Record<string, ValidatorFn[]>, key?: string) {
  if (Array.isArray(value)) {
    return new FormArray(value.map((item) => getForm(item, validators)), validators?.[key] || []);
  }

  if (value && typeof value === 'object' && !(value instanceof Date)) {
    return new FormGroup(Object.keys(value).reduce((acc, key) => {
      acc[key] = getForm(value[key], validators, key);
      return acc;
    }, {}), validators?.[key] || []);
  }

  return new FormControl(value, validators?.[key] || []);
}

function hasChanges(form: FormArray | FormGroup | FormControl | AbstractControl<any, any>, initialState: any, deepComparison: boolean): boolean {
  if (form instanceof FormArray) {
    return form.getRawValue()?.length !== initialState?.length || form.controls.some((control, index) => hasChanges(control, initialState?.[index], deepComparison));
  }

  if (form instanceof FormGroup) {
    return Object.keys(form.controls).some((key) => hasChanges(form.get(key), initialState?.[key], deepComparison));
  }

  if (deepComparison){
    return form.getRawValue() !== initialState?.value || form.disabled !== initialState?.disabled
  }
  return form.dirty && form.getRawValue() !== initialState?.value;
}

function getValue(form: FormArray | FormGroup | FormControl | AbstractControl<any, any>) {
  if (form instanceof FormArray) {
    return form.controls.map(control => getValue(control));
  }

  if (form instanceof FormGroup) {
    return Object.keys(form.controls).reduce((acc, key) => {
      acc[key] = getValue(form.get(key));
      return acc;
    }, {});
  }

  return { value: form.getRawValue(), disabled: form.disabled };
}


export const updateFormArray = (formArray: FormArray, newArray: any[], validators?: Record<string, ValidatorFn[]>): void => {
  formArray?.clear();
  formArray?.reset([]);
  newArray?.forEach(item => {
    const formItem = createFormGroup(item, validators);
    formArray.push(formItem);
  })
}